/**
 * Rock UCS (No Strain) — Test Parameters tab.
 *
 * BUG-2026-0138 rebuild: the sheet now mirrors TCS / UCS-with-strain, reusing
 * Test A's section components (shared, not cloned):
 *   1. Specimen Parameters  — rock-ucs SpecimenParametersSection over a merged
 *      specimen-1 + general view (one row of diameters, one row of lengths,
 *      condition, mass, Leeb, prepared-by, derived panel)
 *   2. Conformity & ASTM Shape Tolerance — rock-ucs ConformitySection: 11-point
 *      dial entry per end per diameter line, live pass/fail dashboard with
 *      values, inline conformity charts, auto report statement
 *   3. Ultrasonic Velocity  — rock-ucs UltrasonicSection: 4 velocity/time
 *      readings, consistency checker, reported averages
 *   4. Compression Test Setup — machine / test date / tested-by + Test B's
 *      Rate of Loading (kN/s) with derived stress rate + ASTM band
 *
 * REMOVED vs the pre-0138 sheet (items 1–4): Rock Description, Preparation,
 * the setup Core Size box (core size lives in the Conformity section, like
 * Test A), and Number of Specimens (always exactly 1).
 *
 * The test always has exactly ONE specimen (item 4) — this tab edits
 * specimen 1 only.
 */
import { useMemo } from 'react';
import { Box } from '@mui/material';
import type { RockUCSNSGeneral, RockUCSNSSpecimen } from '../../../types/rockUcsNs';
import type { RockUCSConformityReading, RockUCSUltrasonicReading } from '../../../types/rockUcs';
import {
  buildShapeToleranceStatement,
  computeConformityPreview,
} from '../rock-ucs/computations';
import {
  SpecimenParametersSection,
  type SpecimenParameterFields,
} from '../rock-ucs/sections/SpecimenParametersSection';
import { ConformitySection } from '../rock-ucs/sections/ConformitySection';
import { UltrasonicSection } from '../rock-ucs/sections/UltrasonicSection';
import { UcsNsTestSetupSection } from './sections/UcsNsTestSetupSection';
import { computeUcsNsSpecimenPreview } from './computations';

interface Props {
  general: RockUCSNSGeneral;
  /** The single specimen (specimen 1 — BUG-2026-0138 item 4). */
  specimen: RockUCSNSSpecimen;
  conformity: RockUCSConformityReading[];
  ultrasonic: RockUCSUltrasonicReading[];
  onGeneralChange: (field: keyof RockUCSNSGeneral, value: RockUCSNSGeneral[keyof RockUCSNSGeneral]) => void;
  onSpecimenChange: (field: keyof RockUCSNSSpecimen, value: RockUCSNSSpecimen[keyof RockUCSNSSpecimen]) => void;
  onConformityChange: (rows: RockUCSConformityReading[]) => void;
  onUltrasonicChange: (rows: RockUCSUltrasonicReading[]) => void;
  readOnly?: boolean;
}

/** Specimen-parameter fields that live on the General row (the rest are per-specimen). */
const GENERAL_FIELDS = new Set<keyof SpecimenParameterFields>(['specimenCondition', 'preparedBy']);

export const RockUCSNSParametersTab = ({
  general,
  specimen,
  conformity,
  ultrasonic,
  onGeneralChange,
  onSpecimenChange,
  onConformityChange,
  onUltrasonicChange,
  readOnly,
}: Props) => {
  const preview = useMemo(() => computeUcsNsSpecimenPreview(specimen), [specimen]);

  // Merged view for the shared SpecimenParametersSection: geometry/mass/Leeb
  // come from specimen 1; condition + prepared-by live on General.
  const specimenParamFields: SpecimenParameterFields = useMemo(() => ({
    diameter1: specimen.diameter1, diameter2: specimen.diameter2, diameter3: specimen.diameter3,
    diameter4: specimen.diameter4, diameter5: specimen.diameter5, diameter6: specimen.diameter6,
    length1: specimen.length1, length2: specimen.length2, length3: specimen.length3, length4: specimen.length4,
    massG: specimen.massG,
    leebHardnessEnds: specimen.leebHardnessEnds,
    leebHardnessEdge: specimen.leebHardnessEdge,
    specimenCondition: general.specimenCondition,
    preparedBy: general.preparedBy,
  }), [specimen, general.specimenCondition, general.preparedBy]);

  const onSpecimenParamChange = (field: keyof SpecimenParameterFields, value: number | string | null) => {
    if (GENERAL_FIELDS.has(field)) {
      onGeneralChange(field as keyof RockUCSNSGeneral, value);
    } else {
      onSpecimenChange(field as keyof RockUCSNSSpecimen, value);
    }
  };

  const conformityPreview = useMemo(() => {
    const series = [] as { endNumber: number; diameterLine: number; points: { positionMm: number; valueMm: number | null }[] }[];
    for (const endNumber of [1, 2]) {
      for (const diameterLine of [1, 2]) {
        const points = conformity
          .filter((r) => r.endNumber === endNumber && r.diameterLine === diameterLine)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((r) => ({ positionMm: r.positionMm, valueMm: r.valueMm }));
        if (points.length > 0) series.push({ endNumber, diameterLine, points });
      }
    }
    return computeConformityPreview({
      series,
      sideStraightnessMm: general.sideStraightnessMm,
      lOverD: preview.lOverD,
      lOverDPass: preview.lOverDPass,
    });
  }, [conformity, general.sideStraightnessMm, preview.lOverD, preview.lOverDPass]);

  const shapeStatement = useMemo(
    () => buildShapeToleranceStatement(conformityPreview.failReasons, general.conformityFailReason),
    [conformityPreview.failReasons, general.conformityFailReason],
  );

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5, pb: 1 }}>
      {/* ── 1. Specimen Parameters ── */}
      <SpecimenParametersSection
        general={specimenParamFields}
        preview={{ ...preview, volumeCm3: preview.volumeMl }}
        onChange={onSpecimenParamChange}
        readOnly={readOnly}
      />

      {/* ── 2. Conformity & ASTM Shape Tolerance ── */}
      <ConformitySection
        general={general}
        conformity={conformity}
        preview={conformityPreview}
        lOverD={preview.lOverD}
        shapeStatement={shapeStatement}
        onChange={(field, value) => onGeneralChange(field as keyof RockUCSNSGeneral, value)}
        onConformityChange={onConformityChange}
        readOnly={readOnly}
      />

      {/* ── 3. Ultrasonic Velocity ── */}
      <UltrasonicSection
        general={general}
        ultrasonic={ultrasonic}
        onChange={(field, value) => onGeneralChange(field as keyof RockUCSNSGeneral, value)}
        onUltrasonicChange={onUltrasonicChange}
        readOnly={readOnly}
      />

      {/* ── 4. Compression Test Setup ── */}
      <UcsNsTestSetupSection
        general={general}
        specimen={specimen}
        preview={preview}
        onGeneralChange={onGeneralChange}
        onSpecimenChange={onSpecimenChange}
        readOnly={readOnly}
      />
    </Box>
  );
};
